import React, { useEffect, useState } from "react";
import axios from "axios";
import { Flame, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TrendingBanner — public showcase of the top-selling products across the
 * store in the last 7 days. Hides itself when the API returns no rows.
 *
 * Props:
 *   priceKind: "retail" | "wholesale" — which price to show on the tile.
 *              Sub-portals show retail; wholesale client portals show wholesale.
 *   onLoaded:  (ids: string[]) => void — parent gets the trending product IDs
 *              so it can render a "Trending" badge on matching product cards.
 *   tone:      "brand" (red on white) | "dark" (amber on black) — sub-portals
 *              use dark to match their inverted theme.
 */
export default function TrendingBanner({ priceKind = "retail", onLoaded, tone = "brand" }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/trending-products`)
      .then((r) => {
        if (!alive) return;
        setRows(r.data || []);
        if (onLoaded) onLoaded((r.data || []).map((p) => p.id));
      })
      .catch(() => setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [onLoaded]);

  if (loading || rows.length === 0) return null;

  const isDark = tone === "dark";
  const containerCls = isDark
    ? "border-amber-400/50 bg-gradient-to-br from-neutral-900 via-black to-neutral-900 text-white"
    : "border-amber-300 bg-gradient-to-br from-amber-50 via-white to-white";
  const eyebrowCls = isDark ? "text-amber-400" : "text-amber-700";
  const tileBorder = isDark ? "border-amber-500/40 bg-white/5" : "border-amber-200 bg-white";
  const nameCls = isDark ? "text-white" : "text-[var(--ink)]";
  const priceCls = isDark ? "text-amber-300" : "text-[var(--brand)]";

  return (
    <Card className={`p-5 mb-4 border ${containerCls}`} data-testid="trending-banner">
      <div className="flex items-center gap-2 mb-3">
        <Flame size={16} className="text-amber-500" />
        <p className={`label-eyebrow ${eyebrowCls}`}>Trending This Week</p>
        <span className={`text-[10px] ${isDark ? "text-white/60" : "text-gray-500"} ml-1`}>
          Top {rows.length} · last 7 days
        </span>
      </div>
      <div
        className={`grid gap-3 ${
          rows.length === 1
            ? "grid-cols-1"
            : rows.length === 2
              ? "grid-cols-2"
              : "grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-5"
        }`}
        data-testid="trending-grid"
      >
        {rows.map((p) => (
          <div
            key={p.id}
            className={`rounded-lg border p-2 flex flex-col items-center text-center ${tileBorder}`}
            data-testid={`trending-tile-${p.id}`}
          >
            {p.image_url ? (
              <img
                src={p.image_url.startsWith("/") ? `${API}${p.image_url}` : p.image_url}
                alt={p.name}
                className="w-full h-24 object-cover rounded"
              />
            ) : (
              <div className={`w-full h-24 rounded flex items-center justify-center ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                <TrendingUp size={20} className="text-amber-500" />
              </div>
            )}
            <p className={`mt-2 text-xs font-bold capitalize leading-tight line-clamp-2 ${nameCls}`}>{p.name}</p>
            <p className={`text-sm font-bold tabular-nums mt-1 ${priceCls}`}>
              ${(priceKind === "wholesale" ? p.wholesale_price : p.retail_price).toFixed(2)}
            </p>
            <p className={`text-[10px] ${isDark ? "text-white/50" : "text-gray-400"} tabular-nums`}>
              {p.units_sold} sold this week
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}
